import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import React, { lazy, Suspense } from "react";
import ReactDOM from "react-dom/client";
import {
  createBrowserRouter,
  isRouteErrorResponse,
  Navigate,
  RouterProvider,
  useRouteError,
} from "react-router-dom";

import App from "./App";
import LegacyWeightResearchRedirect from './components/LegacyWeightResearchRedirect';
import LegacyResearchRedirect from './components/LegacyResearchRedirect';
import { AuthGuard } from "./auth/AuthGuard";
import { AuthProvider } from "./auth/AuthContext";
import { InterfaceModeProvider } from "./auth/InterfaceModeContext";
import { ModuleGuard } from "./auth/ModuleGuard";
import "./index.css";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import { marketStale } from "./lib/marketClock";
import { toast, Toaster } from "./ui/toast";
import { track, usageGeneration } from "./usage";
import { featurePath } from "./lib/featureUsage";

/**
 * 部署后旧 SW / 浏览器缓存里的 index.html 仍指向上一版构建的 chunk hash，
 * 而该 hash 文件已被新部署覆盖删掉 → 动态 import 拿到 404。重试无用（文件永远不在了）。
 * 唯一解法：整页 reload 一次，重新 network-first 拉新 index.html → 拿到新 chunk hash。
 *
 * 用 sessionStorage 做"一次性"闸：刚 reload 过(≤20s)还失败，说明不是 stale-chunk 而是
 * 真坏了 → 不再 reload，放行抛错让 ErrorBoundary 兜底，避免无限刷新循环。
 *
 * ⚠ 与 feedback_sw_no_auto_reload 不冲突：那条禁的是 SW activate/controllerchange 时自动
 * reload（会撞登录表单 race）；这里只在 route chunk 硬失败、页面已不可用时 reload，且
 * LoginPage 是 eager import 不走 lazy，不存在表单状态被清的风险。
 */
const _CHUNK_RELOAD_KEY = "ws_chunk_reload_at";
const _CHUNK_RELOAD_WINDOW_MS = 20_000;

function RouteErrorPage() {
  const error = useRouteError();
  const message = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
      ? error.message
      : "未知页面错误";

  React.useEffect(() => {
    track("frontend_error", "route_render_failed", { message });
  }, [message]);

  return (
    <main className="min-h-screen grid place-items-center bg-zinc-50 px-4 text-zinc-900 dark:bg-zinc-950 dark:text-zinc-100">
      <section className="w-full max-w-lg rounded-xl border border-rose-200 bg-white p-6 shadow-sm dark:border-rose-900 dark:bg-zinc-900">
        <h1 className="text-lg font-semibold">页面遇到异常</h1>
        <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300">
          当前操作没有完成。你可以刷新后重试；如果问题持续出现，请把下方错误信息发给开发人员。
        </p>
        <pre className="mt-3 max-h-32 overflow-auto rounded bg-zinc-100 p-3 text-xs whitespace-pre-wrap dark:bg-zinc-800">
          {message}
        </pre>
        <div className="mt-4 flex gap-2">
          <button
            onClick={() => window.location.reload()}
            className="rounded bg-zinc-900 px-3 py-1.5 text-sm text-white dark:bg-white dark:text-zinc-900"
          >
            刷新页面
          </button>
          <a href="/" className="rounded border px-3 py-1.5 text-sm dark:border-zinc-700">
            返回首页
          </a>
        </div>
      </section>
    </main>
  );
}

function _reloadOnceForStaleChunk(): boolean {
  try {
    const last = Number(sessionStorage.getItem(_CHUNK_RELOAD_KEY) || 0);
    if (last && Date.now() - last < _CHUNK_RELOAD_WINDOW_MS) {
      // 刚刚已经 reload 过一次，还是失败 → 别再循环
      return false;
    }
    sessionStorage.setItem(_CHUNK_RELOAD_KEY, String(Date.now()));
    window.location.reload();
    return true;
  } catch {
    // sessionStorage 不可用（隐私模式等）→ 放弃 reload，让上层抛错
    return false;
  }
}

/**
 * lazy() 的网络抖动安全版：动态 import 失败时按 backoff 重试 3 次。
 * 解决"用户切到一个 page，刚好碰上瞬时网络抖 / SW 升级时新 chunk 还没 publish"
 * 导致的白屏。重试仍失败 → 大概率是 stale-chunk(404)，整页 reload 一次拿新 index.html；
 * reload 后仍失败才抛错（Suspense / ErrorBoundary 兜底）。
 */
function lazyWithRetry<T extends React.ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    let lastErr: unknown;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const mod = await factory();
        // 成功载入 → 清掉闸，让下次部署的 stale-chunk 还能再 reload 一次
        try { sessionStorage.removeItem(_CHUNK_RELOAD_KEY); } catch { /* ignore */ }
        return mod;
      } catch (e) {
        lastErr = e;
        await new Promise((r) => setTimeout(r, 500 * 2 ** attempt));
      }
    }
    track("frontend_error", "lazy_chunk_load_failed", {
      message: String((lastErr as Error)?.message ?? lastErr),
    });
    // stale-chunk 兜底：整页 reload 一次（带闸防循环）。reload 会触发导航中断，
    // 返回一个永不 resolve 的 promise 让当前渲染挂起到页面卸载，避免闪一下 ErrorBoundary。
    if (typeof window !== "undefined" && _reloadOnceForStaleChunk()) {
      return new Promise<{ default: T }>(() => { /* 等待 reload 卸载页面 */ });
    }
    throw lastErr;
  });
}

// 仅 LoginPage 直接 import — 进站首屏必须有；其他全 lazy 按需载
const AccountsPage = lazyWithRetry(() => import("./pages/AccountsPage"));
const ModulesPage = lazyWithRetry(() => import("./pages/ModulesPage"));
const AskPage = lazyWithRetry(() => import("./pages/AskPage"));
const CopilotPage = lazyWithRetry(() => import("./pages/CopilotPage"));
const BlockTradesPage = lazyWithRetry(() => import("./pages/BlockTradesPage"));
const LabPage = lazyWithRetry(() => import("./pages/LabPage"));
const AdaptiveResearchPage = lazyWithRetry(() => import("./pages/AdaptiveResearchPage"));
const ResearchPublishingPage = lazyWithRetry(() => import("./pages/ResearchPublishingPage"));
const PairsPage = lazyWithRetry(() => import("./pages/PairsPage"));
const CointScanPage = lazyWithRetry(() => import("./pages/CointScanPage"));
const QuantAgentsPage = lazyWithRetry(() => import("./pages/QuantAgentsPage"));
const BriefingPage = lazyWithRetry(() => import("./pages/BriefingPage"));
const MarketBriefPage = lazyWithRetry(() => import("./pages/MarketBriefPage"));
const RpsPage = lazyWithRetry(() => import("./pages/RpsPage"));
const RpsBacktestPage = lazyWithRetry(() => import("./pages/RpsBacktestPage"));
const RotationPage = lazyWithRetry(() => import("./pages/RotationPage"));
const IndustryDetailPage = lazyWithRetry(() => import("./pages/IndustryDetailPage"));
const EtfFlowPage = lazyWithRetry(() => import("./pages/EtfFlowPage"));
const LibraryPage = lazyWithRetry(() => import("./pages/LibraryPage"));
const TopgunPage = lazyWithRetry(() => import("./pages/TopgunPage"));
const ThemeRadarPage = lazyWithRetry(() => import("./pages/ThemeRadarPage"));
const UsagePage = lazyWithRetry(() => import("./pages/UsagePage"));
const SwingPage = lazyWithRetry(() => import("./pages/SwingPage"));
const GridPage = lazyWithRetry(() => import("./pages/GridPage"));
const TRecapPage = lazyWithRetry(() => import("./pages/TRecapPage"));
const RiskPage = lazyWithRetry(() => import("./pages/RiskPage"));
const JournalPage = lazyWithRetry(() => import("./pages/JournalPage"));
const HomePage = lazyWithRetry(() => import("./pages/HomePage"));
const MoreToolsPage = lazyWithRetry(() => import("./pages/MoreToolsPage"));

// 登录后用户大概率去 Dashboard — 浏览器 idle 时预取，让首次导航瞬时
if (typeof window !== "undefined") {
  const _kickDashboard = () => {
    import("./pages/HomePage").catch(() => undefined);
  };
  // Login/register must stay quiet while the user types. Dashboard prefetch is useful only
  // inside the authenticated application shell.
  if (window.location.pathname !== "/login" && window.location.pathname !== "/register") {
    const ric = (window as unknown as { requestIdleCallback?: (cb: () => void) => void }).requestIdleCallback;
    if (ric) ric(_kickDashboard);
    else setTimeout(_kickDashboard, 1500);
  }
}
const NewsListPage = lazyWithRetry(() => import("./pages/NewsListPage"));
const WatchNewsPage = lazyWithRetry(() => import("./pages/WatchNewsPage"));
const AlertsPage = lazyWithRetry(() => import("./pages/AlertsPage"));
const IntradayTrackingPage = lazyWithRetry(() => import("./pages/IntradayTrackingPage"));
const MlForwardTrackingPage = lazyWithRetry(() => import("./pages/MlForwardTrackingPage"));
const MlJumpsPage = lazyWithRetry(() => import("./pages/MlJumpsPage"));
const PatternDetailPage = lazyWithRetry(() => import("./pages/PatternDetailPage"));
const PatternsListPage = lazyWithRetry(() => import("./pages/PatternsListPage"));
const AnalogForecastPage = lazyWithRetry(() => import("./pages/AnalogForecastPage"));
const NotificationsPage = lazyWithRetry(() => import("./pages/NotificationsPage"));
const ScannerPage = lazyWithRetry(() => import("./pages/ScannerPage"));
const PositionsPage = lazyWithRetry(() => import("./pages/PositionsPage"));
const BrokerTradingPage = lazyWithRetry(() => import("./pages/BrokerTradingPage"));
const McpTokensPage = lazyWithRetry(() => import("./pages/McpTokensPage"));
const SkillHistoryPage = lazyWithRetry(() => import("./pages/SkillHistoryPage"));
const SkillCenterPage = lazyWithRetry(() => import("./pages/SkillCenterPage"));
const AdminInvitesPage = lazyWithRetry(() => import("./pages/AdminInvitesPage"));
const LlmTrainingPage = lazyWithRetry(() => import("./pages/LlmTrainingPage"));
const ErrorsPage = lazyWithRetry(() => import("./pages/ErrorsPage"));
const SettingsPage = lazyWithRetry(() => import("./pages/SettingsPage"));
const WeChatSettingsPage = lazyWithRetry(() => import("./pages/WeChatSettingsPage"));
const WeChatLogPage = lazyWithRetry(() => import("./pages/WeChatLogPage"));
const WatchlistPage = lazyWithRetry(() => import("./pages/WatchlistPage"));
const StockDetailPage = lazyWithRetry(() => import("./pages/StockDetailPage"));
const StockListPage = lazyWithRetry(() => import("./pages/StockListPage"));

// 简易 page-load skeleton（lazy chunk 拉取中显示）
function PageSkeleton() {
  return (
    <div className="space-y-3 animate-pulse">
      <div className="h-7 w-1/3 bg-zinc-200 dark:bg-zinc-800 rounded" />
      <div className="h-4 w-1/2 bg-zinc-200 dark:bg-zinc-800 rounded" />
      <div className="h-32 bg-zinc-200 dark:bg-zinc-800 rounded mt-4" />
      <div className="h-32 bg-zinc-200 dark:bg-zinc-800 rounded" />
    </div>
  );
}

const wrap = (node: React.ReactNode) => <Suspense fallback={<PageSkeleton />}>{node}</Suspense>;

// 持久化 buster：__APP_VERSION__ 由 vite define 注入；切版本时 localStorage 缓存全废
declare const __APP_VERSION__: string;

const actionRuns = new WeakMap<object, { feature: string; started: number; generation: number }>();
const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onMutate: (_variables, mutation) => {
      const feature = featurePath(window.location.pathname);
      if (!feature) return;
      actionRuns.set(mutation, { feature, started: performance.now(), generation: usageGeneration() });
      track("action_start", `${feature}:request`);
    },
    onSettled: (_data, error, _variables, _context, mutation) => {
      const run = actionRuns.get(mutation);
      if (!run || run.generation !== usageGeneration()) return;
      track("action_result", `${run.feature}:request`, { outcome: error ? "error" : "success", duration_ms: Math.round(performance.now() - run.started) });
      actionRuns.delete(mutation);
    },
  }),
  defaultOptions: {
    queries: {
      // 市场时钟感知：盘中 30s，盘后/深夜/周末自动放大（配合持久化=静默期刷新几乎零请求）
      staleTime: marketStale(30_000),
      // gcTime（v5 = 旧的 cacheTime）：query 不再使用后多久从内存/storage 清理
      // 持久化要求至少跟 maxAge 同量级；24h 配合 maxAge 24h
      gcTime: 1000 * 60 * 60 * 24,
      refetchOnWindowFocus: false,
      // retry 2 次 + 指数 backoff（500ms → 1.5s → 3s）；最多 3 次尝试
      retry: 2,
      retryDelay: (attempt) => Math.min(500 * 2 ** attempt, 4000),
    },
    mutations: {
      // mutation 默认不 retry（避免重复下单 / 重复扣 quota 类副作用）
      retry: false,
    },
  },
  // 全局兜底：query 失败 → toast 一行；UI 仍可 catch 自定义提示
  queryCache: new QueryCache({
    onError: (err) => {
      const msg = (err as Error)?.message || "未知错误";
      // 401 / 403 不打扰（AuthGuard 接管跳登录）
      if (msg.startsWith("401") || msg.startsWith("403")) return;
      toast.error(`加载失败：${msg.slice(0, 100)}`);
    },
  }),
});

// 持久化到 localStorage — 冷启动也能立刻看到上次数据（再后台 refetch）
//
// ⚠️ createSyncStoragePersister 每次有新 query 结果落库时同步调 JSON.stringify +
// localStorage.setItem。首页 mount 同时有 7+ 个 query resolve → 主线程连续被阻塞 7 次 →
// 鼠标卡死。这里加 debounce（trailing-only）：短时间内多次 persist 只写最后一次。
const _rawPersister = createSyncStoragePersister({
  storage: typeof window !== "undefined" ? window.localStorage : undefined,
  key: "ws_public_query_cache_v2",
  // 失败静默（如 localStorage 满 / 隐私模式）
});

let _persistTimer: ReturnType<typeof setTimeout> | null = null;
const _PERSIST_DEBOUNCE_MS = 1_200;
const persister = {
  ..._rawPersister,
  persistClient(client: Parameters<typeof _rawPersister.persistClient>[0]) {
    if (_persistTimer) clearTimeout(_persistTimer);
    _persistTimer = setTimeout(() => {
      _rawPersister.persistClient(client);
      _persistTimer = null;
    }, _PERSIST_DEBOUNCE_MS);
  },
};

window.addEventListener("whaleseek:clear-session-cache", () => {
  if (_persistTimer) clearTimeout(_persistTimer);
  _persistTimer = null;
  _rawPersister.removeClient();
  try { localStorage.removeItem("ws_query_cache"); } catch { /* storage unavailable */ }
});

// Only persist small, first-screen queries. Persisting large lists and time series makes
// localStorage synchronously stringify the whole cache and can block pointer input.
const PERSISTED_QUERY_KEYS = new Set([
  "market-index-realtime",
  "market-brief-latest",
]);

const router = createBrowserRouter([
  { path: "/login", element: <LoginPage />, errorElement: <RouteErrorPage /> },
  { path: "/register", element: <RegisterPage />, errorElement: <RouteErrorPage /> },
  {
    path: "/",
    errorElement: <RouteErrorPage />,
    element: (
      <AuthGuard>
        <App />
      </AuthGuard>
    ),
    children: [
      { index: true, element: wrap(<HomePage />) },
      { path: "more-tools", element: wrap(<MoreToolsPage />) },
      // core (forced, no guard)
      { path: "stocks", element: wrap(<StockListPage />) },
      { path: "block-trades", element: wrap(<BlockTradesPage />) },
      { path: "stocks/:tsCode", element: wrap(<StockDetailPage />) },
      { path: "watchlist", element: wrap(<WatchlistPage />) },
      { path: "scanner", element: wrap(<ScannerPage />) },
      // briefing
      { path: "briefing", element: <ModuleGuard>{wrap(<BriefingPage />)}</ModuleGuard> },
      { path: "market-brief", element: <ModuleGuard>{wrap(<MarketBriefPage />)}</ModuleGuard> },
      { path: "rps", element: <ModuleGuard>{wrap(<RpsPage />)}</ModuleGuard> },
      { path: "rps/backtest", element: <ModuleGuard>{wrap(<RpsBacktestPage />)}</ModuleGuard> },
      { path: "rotation", element: <ModuleGuard>{wrap(<RotationPage />)}</ModuleGuard> },
      { path: "industries/:industry", element: <ModuleGuard>{wrap(<IndustryDetailPage />)}</ModuleGuard> },
      { path: "etf", element: <ModuleGuard>{wrap(<EtfFlowPage />)}</ModuleGuard> },
      { path: "indicators", element: <LegacyResearchRedirect /> },
      { path: "library", element: wrap(<LibraryPage />) },
      { path: "topgun", element: <ModuleGuard>{wrap(<TopgunPage />)}</ModuleGuard> },
      { path: "theme-radar", element: <ModuleGuard>{wrap(<ThemeRadarPage />)}</ModuleGuard> },
      { path: "ask", element: <ModuleGuard>{wrap(<AskPage />)}</ModuleGuard> },
      { path: "copilot", element: <ModuleGuard>{wrap(<CopilotPage />)}</ModuleGuard> },
      { path: "quant-agents", element: <ModuleGuard>{wrap(<QuantAgentsPage />)}</ModuleGuard> },
      { path: "screen-runs", element: <LegacyResearchRedirect /> },
      // news
      { path: "news", element: <ModuleGuard>{wrap(<NewsListPage />)}</ModuleGuard> },
      { path: "watch-news", element: <ModuleGuard>{wrap(<WatchNewsPage />)}</ModuleGuard> },
      // patterns
      { path: "patterns", element: <ModuleGuard>{wrap(<PatternsListPage />)}</ModuleGuard> },
      { path: "patterns/:id", element: <ModuleGuard>{wrap(<PatternDetailPage />)}</ModuleGuard> },
      { path: "analog", element: <ModuleGuard>{wrap(<AnalogForecastPage />)}</ModuleGuard> },
      // ml
      { path: "ml/experiments", element: <LegacyResearchRedirect /> },
      { path: "ml/compare", element: <LegacyResearchRedirect /> },
      { path: "ml/experiments/:id", element: <LegacyResearchRedirect /> },
      { path: "ml/forward-tracking", element: <ModuleGuard>{wrap(<MlForwardTrackingPage />)}</ModuleGuard> },
      { path: "ml/jumps", element: <ModuleGuard>{wrap(<MlJumpsPage />)}</ModuleGuard> },
      { path: "auto-tune", element: <LegacyResearchRedirect /> },
      // alerts
      { path: "alerts", element: <ModuleGuard>{wrap(<AlertsPage />)}</ModuleGuard> },
      { path: "intraday-tracking", element: <ModuleGuard>{wrap(<IntradayTrackingPage />)}</ModuleGuard> },
      { path: "notifications", element: <ModuleGuard>{wrap(<NotificationsPage />)}</ModuleGuard> },
      // positions
      { path: "positions", element: <ModuleGuard>{wrap(<PositionsPage />)}</ModuleGuard> },
      { path: "broker-trading", element: <ModuleGuard>{wrap(<BrokerTradingPage />)}</ModuleGuard> },
      { path: "paper", element: <Navigate to="/quant-agents" replace /> },
      { path: "swing", element: <ModuleGuard>{wrap(<SwingPage />)}</ModuleGuard> },
      { path: "grid", element: <ModuleGuard>{wrap(<GridPage />)}</ModuleGuard> },
      { path: "grid/research", element: <LegacyResearchRedirect /> },
      { path: "t-recap", element: <ModuleGuard>{wrap(<TRecapPage />)}</ModuleGuard> },
      { path: "risk", element: <ModuleGuard>{wrap(<RiskPage />)}</ModuleGuard> },
      { path: "journal", element: <ModuleGuard>{wrap(<JournalPage />)}</ModuleGuard> },
      // quant — shared stock pools + research + Agent execution center
      { path: "lab", element: <ModuleGuard>{wrap(<LabPage />)}</ModuleGuard> },
      { path: "lab/sandbox", element: <LegacyResearchRedirect /> },
      { path: "lab/adaptive", element: <ModuleGuard>{wrap(<AdaptiveResearchPage />)}</ModuleGuard> },
      { path: "lab/research-agents", element: <LegacyResearchRedirect /> },
      { path: "lab/cockpit-weights", element: <LegacyWeightResearchRedirect /> },
      { path: "ml/publishing", element: <ModuleGuard>{wrap(<ResearchPublishingPage />)}</ModuleGuard> },
      { path: "lab/pairs", element: <ModuleGuard>{wrap(<PairsPage />)}</ModuleGuard> },
      { path: "lab/pairs/:pairId", element: <ModuleGuard>{wrap(<PairsPage />)}</ModuleGuard> },
      { path: "lab/:basketId", element: <ModuleGuard>{wrap(<LabPage />)}</ModuleGuard> },
      { path: "lab/coint", element: <ModuleGuard>{wrap(<CointScanPage />)}</ModuleGuard> },
      { path: "lab/arena", element: <Navigate to="/quant-agents" replace /> },
      // settings (forced, no guard)
      { path: "settings", element: wrap(<SettingsPage />) },
      { path: "settings/accounts", element: wrap(<AccountsPage />) },
      { path: "settings/modules", element: wrap(<ModulesPage />) },
      { path: "settings/usage", element: wrap(<UsagePage />) },
      { path: "settings/mcp", element: wrap(<McpTokensPage />) },
      { path: "skills/history", element: wrap(<SkillHistoryPage />) },
      { path: "skills", element: wrap(<SkillCenterPage />) },
      { path: "settings/wechat", element: wrap(<WeChatSettingsPage />) },
      { path: "wechat-log", element: wrap(<WeChatLogPage />) },
      { path: "settings/admin/invites", element: wrap(<AdminInvitesPage />) },
      { path: "settings/admin/llm-training", element: wrap(<LlmTrainingPage />) },
      { path: "settings/errors", element: wrap(<ErrorsPage />) },
    ],
  },
]);

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 1000 * 60 * 60 * 24,   // 24h
        buster: __APP_VERSION__,        // 部署 bump 版本 → 旧缓存全废
        dehydrateOptions: {
          shouldDehydrateQuery: (query) => {
            if (query.state.status !== "success") return false;

            const [group, resource] = query.queryKey;
            if (PERSISTED_QUERY_KEYS.has(String(group ?? ""))) return true;

            // Restore the visible pair scan immediately after a browser reload,
            // then validate it in the background. Only active list/meta queries
            // are saved, so filter history and chart time series cannot build up.
            return group === "coint"
              && (resource === "meta" || resource === "anomaly")
              && query.isActive();
          },
        },
      }}
    >
      <AuthProvider>
        <InterfaceModeProvider>
          <RouterProvider router={router} />
        </InterfaceModeProvider>
      </AuthProvider>
      <Toaster />
    </PersistQueryClientProvider>
  </React.StrictMode>,
);

// 主线程长任务遥测（2026-07-18 卡顿排查）：捕捉阻塞 >300ms 的任务 + 30s 汇总
// （次数/总阻塞时长/JS 堆内存）。远程还原"卡的那一刻主线程在干什么"：
// 连续小长任务=渲染循环；单个巨型任务=某次重操作；页面卡但无长任务=卡不在本页。
if (typeof window !== "undefined" && "PerformanceObserver" in window) {
  try {
    let ltCount = 0;
    let ltTotal = 0;
    let ltMax = 0;
    const obs = new PerformanceObserver((list) => {
      for (const e of list.getEntries()) {
        ltCount++;
        ltTotal += e.duration;
        if (e.duration > ltMax) ltMax = e.duration;
        if (e.duration > 1000) {
          track("frontend_error", "longtask_big", {
            duration: Math.round(e.duration),
            pathname: window.location.pathname,
          });
        }
      }
    });
    obs.observe({ entryTypes: ["longtask"] });
    setInterval(() => {
      if (ltCount === 0) return;
      const mem = (performance as unknown as { memory?: { usedJSHeapSize: number } }).memory;
      track("frontend_error", "longtask_summary", {
        count: ltCount,
        total_ms: Math.round(ltTotal),
        max_ms: Math.round(ltMax),
        heap_mb: mem ? Math.round(mem.usedJSHeapSize / 1048576) : null,
        pathname: window.location.pathname,
      });
      ltCount = 0;
      ltTotal = 0;
      ltMax = 0;
    }, 30_000);
  } catch { /* longtask 不支持的浏览器忽略 */ }
}

// 全局前端错误捕获 → usage_event(frontend_error)
// JS 运行时崩溃、未 catch 的 promise reject、资源加载失败 都会到这；
// 同样的 (message, target) 1 分钟内只上报一次防止刷屏。
if (typeof window !== "undefined") {
  const _recentErrKeys = new Map<string, number>();
  function _reportFrontendError(type: string, payload: Record<string, unknown>) {
    const key = `${type}:${String(payload.message ?? "").slice(0, 100)}`;
    const now = Date.now();
    const last = _recentErrKeys.get(key);
    if (last != null && now - last < 60_000) return;
    _recentErrKeys.set(key, now);
    // 清旧记录
    for (const [k, t] of _recentErrKeys) {
      if (now - t > 300_000) _recentErrKeys.delete(k);
    }
    track("frontend_error", type, payload);
  }

  window.addEventListener("error", (e: ErrorEvent) => {
    _reportFrontendError("window_error", {
      message: e.message,
      source: e.filename,
      lineno: e.lineno,
      colno: e.colno,
      stack: e.error?.stack ? String(e.error.stack).slice(0, 1500) : null,
      pathname: window.location.pathname,
    });
  });
  window.addEventListener("unhandledrejection", (e: PromiseRejectionEvent) => {
    const r = e.reason;
    _reportFrontendError("unhandled_rejection", {
      message: r instanceof Error ? r.message : String(r).slice(0, 300),
      stack: r instanceof Error && r.stack ? String(r.stack).slice(0, 1500) : null,
      pathname: window.location.pathname,
    });
  });
}

// SW 周期性自检：浏览器默认 24h 才检查 sw.js 是否更新，自用站点改成 5 分钟。
// 不重复 register（vite-plugin-pwa 已 inject auto-register），仅触发 update()。
// 配合 sw.ts 里 navigation NetworkFirst：用户下次切页就拿到新 index.html。
//
// ⚠ 不在这里 location.reload —— v3/v4 实验过用 message → reload 自动接管，结果
// 撞到用户填登录表时 SW activate 触发 reload 把表单清掉，登录死循环。
// 让 SW 自然 claim，用户**切任意路由**就走 NetworkFirst 拿新 SPA，安全无副作用。
//
// 但加一个 toast：waiting → activated 时提示用户"新版已就绪，刷新查看"，由用户主动决定。
if ("serviceWorker" in navigator) {
  const SW_UPDATE_INTERVAL_MS = 5 * 60 * 1000;

  // 启动时 update 一次
  navigator.serviceWorker.getRegistration()
    .then((reg) => {
      if (!reg) return;
      reg.update().catch(() => undefined);
      // 监听 waiting 状态（vite-plugin-pwa injectManifest 自带）
      reg.addEventListener("updatefound", () => {
        const sw = reg.installing;
        if (!sw) return;
        sw.addEventListener("statechange", () => {
          if (sw.state === "installed" && navigator.serviceWorker.controller) {
            // 旧 SW 还在控制中，新 SW 已 install → 等下次 controllerchange 提示
          }
        });
      });
    })
    .catch(() => undefined);

  // controllerchange：新 SW 真正接管时触发，此时新代码生效，提示用户刷新拿最新 UI
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    const path = window.location.pathname;
    const active = document.activeElement as HTMLElement | null;
    const editing = active instanceof HTMLInputElement
      || active instanceof HTMLTextAreaElement
      || active instanceof HTMLSelectElement
      || active?.isContentEditable === true;
    if (path === "/login" || path === "/register" || editing) {
      toast.info("📦 新版本已就绪，请完成当前输入后刷新页面");
      return;
    }
    window.location.reload();
  });

  // SW 后台重验证完成（网络抖动过去、缓存已 warm）→ 目标化自愈：只重取当前挂载的 query，
  // 命中 warm 缓存秒回 fresh，"离线/滞后"横幅随即消失。用 navigator.onLine 触发不到这种毫秒级
  // 抖动，故改由 SW 的真实信号驱动；debounce 合并抖动风暴，refetchType:active 只刷屏上可见 query。
  // ⚠️ 除 600ms debounce 外再加 60s 节流：网络持续慢时 SW 通知会连续到来，
  // 每次 invalidate 都触发全部 active query refetch → 慢响应又生成新通知 → 永动机
  // （实测把主线程打满、鼠标卡死）。SW v9 已改"内容变了才通知"，这里是第二道保险。
  let _revalTimer: ReturnType<typeof setTimeout> | undefined;
  let _lastRevalAt = 0;
  navigator.serviceWorker.addEventListener("message", (e: MessageEvent) => {
    if ((e.data as { type?: string } | null)?.type !== "ws-revalidated") return;
    clearTimeout(_revalTimer);
    _revalTimer = setTimeout(() => {
      if (Date.now() - _lastRevalAt < 60_000) return;
      _lastRevalAt = Date.now();
      // Mark cached data stale without creating a simultaneous refetch/render storm.
            // Only invalidate first-screen persisted query keys — avoids marking 30+
      // active dashboard queries stale at once, causing a refetch storm.
      for (const key of PERSISTED_QUERY_KEYS) {
        queryClient.invalidateQueries({ queryKey: [key], refetchType: "none" });
      }
    }, 600);
  });

  setInterval(() => {
    navigator.serviceWorker.getRegistration()
      .then((reg) => reg?.update())
      .catch(() => undefined);
  }, SW_UPDATE_INTERVAL_MS);
}
